/**
 * CPG AI Lab — Packaging AI (first toolkit in the 5-section layout)
 *
 * Five-section pattern (repeatable across future Lab modules):
 *   1. Overview           — what this toolkit does, prerequisites
 *   2. Workflow           — the recommended step-by-step process
 *   3. Recommended Tools  — curated 3rd-party picks (text/design/video)
 *   4. Prompt Builder     — reads the founder's Brand Profile and
 *                           assembles a category-aware packaging prompt
 *                           they can copy into any of the recommended
 *                           image tools
 *   5. Examples           — before/after mockups + prompt patterns
 *
 * Product decision: this page does NOT call an image model directly —
 * that keeps the surface fast, provider-agnostic, and free (no per-user
 * Emergent LLM cost per packaging brainstorm). Founders paste the built
 * prompt into whichever tool matches their brand aesthetic.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "../../components/AppShell";
import PageHero from "../../components/PageHero";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Copy, Sparkles, Palette, FileText, Video, ExternalLink,
  CheckCircle2, ChevronRight, Layers, Wand2, ImageIcon, Info,
} from "lucide-react";

// ── Curated Recommended Tools ─────────────────────────────────────────
// Best-in-class picks for a CPG packaging workflow. Split by discipline
// so a founder can pick the ONE tool per column that fits their budget
// / team skill instead of stacking five subscriptions. Vetted by the
// Warren team; refresh quarterly.
export const RECOMMENDED_TOOLS = {
  text: [
    { name: "Claude Sonnet 5",   url: "https://claude.ai",        blurb: "Best-in-class on-pack copy, claims wording, and hero-line brainstorming.", pick: "Editor's pick" },
    { name: "ChatGPT",           url: "https://chat.openai.com",  blurb: "Fast for name generation, callout ladders, and back-panel benefit copy." },
    { name: "Jasper",            url: "https://jasper.ai",        blurb: "Marketing microcopy at scale — good if you're batch-generating SKUs." },
    { name: "Copy.ai",           url: "https://copy.ai",          blurb: "Product-description templates tuned for retail listings and Amazon copy." },
  ],
  design: [
    { name: "Midjourney",        url: "https://midjourney.com",   blurb: "Best photorealism for hero mockups + lifestyle scenes. Slack workflow.", pick: "Editor's pick" },
    { name: "Ideogram",          url: "https://ideogram.ai",      blurb: "The one AI image tool that renders LEGIBLE on-pack typography." },
    { name: "Adobe Firefly",     url: "https://firefly.adobe.com",blurb: "Commercially safe assets, inpainting for label edits, generative fill." },
    { name: "Canva Magic Studio",url: "https://canva.com",        blurb: "Fastest way to drop your AI-generated pack into a hi-res label template." },
    { name: "Photoroom",         url: "https://photoroom.com",    blurb: "One-tap background removal + on-white product shots for retailer decks." },
  ],
  video: [
    { name: "Runway Gen-3",      url: "https://runwayml.com",     blurb: "Product hero video and short 5–10 s brand cinematics.", pick: "Editor's pick" },
    { name: "Pika Labs",         url: "https://pika.art",         blurb: "Snappy 3–4 s social loops. Great for Reels/TikTok cover clips." },
    { name: "CapCut AI",         url: "https://capcut.com",       blurb: "Auto-cut, captions, and B-roll — turn a phone shoot into finished ads." },
  ],
};

// ── Workflow steps ────────────────────────────────────────────────────
const WORKFLOW = [
  { title: "Confirm your Brand Profile",
    body: "Category, positioning, and target consumer must be authored — they seed every prompt.",
    cta: { label: "Open Brand Profile", to: "/ai-lab/brand-profile" } },
  { title: "Pick a packaging archetype",
    body: "Minimal apothecary, retro grocery, playful DTC, premium matte — the archetype anchors typography, palette, and lighting.",
    cta: null },
  { title: "Assemble the prompt",
    body: "Use the Prompt Builder below. It merges your Brand Profile with the archetype and outputs a ready-to-paste prompt.",
    cta: null },
  { title: "Generate in your chosen tool",
    body: "Paste into Midjourney, Ideogram, or Firefly. Ideogram wins when on-pack text matters; Midjourney wins on photorealism.",
    cta: null },
  { title: "Iterate + refine",
    body: "Vary lighting (studio / natural / editorial) and background (linen / marble / kitchen) as v2/v3 prompts.",
    cta: null },
  { title: "Composite in Canva or Photoshop",
    body: "Drop the AI hero into a label template, add legal copy, and export for buyer decks or a Shopify PDP.",
    cta: null },
];

const ARCHETYPES = [
  { key: "minimal",   label: "Minimal apothecary", desc: "Muted neutrals, tall serif, generous whitespace." },
  { key: "retro",     label: "Retro grocery",       desc: "Warm hand-drawn, saturated primaries, playful copy." },
  { key: "premium",   label: "Premium matte",       desc: "Dark palette, gold foil accents, editorial photography." },
  { key: "playful",   label: "Playful DTC",         desc: "Bold pastels, chunky sans, high-energy call-outs." },
  { key: "clinical",  label: "Clinical wellness",   desc: "Clean white, clinical typography, evidence-forward badges." },
];

const EXAMPLES = [
  { archetype: "Minimal apothecary",
    before: "Bland stock photo of an amber bottle on a plain background.",
    after:  "Amber Corvo Wellness bottle on Carrara marble, morning window light, soft focus fresh mint leaves foreground, editorial food-photography aesthetic, cinematic 4:5 aspect.",
    note:   "Notice the specificity: material (marble), light (morning window), prop (mint), and framing (4:5).",
  },
  { archetype: "Playful DTC",
    before: "Generic packaging concept on a white studio background.",
    after:  "Two colorful Amber & Oak snack pouches mid-jump against a bold coral gradient, confetti burst, glossy plastic reflections, Instagram-first product hero, 1:1 aspect.",
    note:   "Motion + energy + brand-color-first background reads as DTC. Great for paid social hero images.",
  },
];

export default function PackagingAILab() {
  const [profile, setProfile] = useState(null);
  const [archetype, setArchetype] = useState("minimal");
  const [productType, setProductType] = useState("");
  const [container, setContainer] = useState("bottle");
  const [surfaceMood, setSurfaceMood] = useState("marble");
  const [lighting, setLighting] = useState("morning window");
  const [aspect, setAspect] = useState("4:5");
  const [extras, setExtras] = useState("");

  useEffect(() => {
    api.get("/brand-profile").then((r) => setProfile(r.data || null)).catch(() => setProfile(null));
  }, []);

  // Reactive prompt assembly — the "Prompt Builder" of the 5-section layout.
  // Deliberately paragraph-form (not JSON) so it drops cleanly into any of
  // the recommended image tools without further wrangling.
  const prompt = useMemo(() => buildPrompt({
    profile, archetype, productType, container, surfaceMood, lighting, aspect, extras,
  }), [profile, archetype, productType, container, surfaceMood, lighting, aspect, extras]);

  const readyToBuild = !!(profile?.brand && profile?.category);

  const copyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt);
      toast.success("Prompt copied — paste into Midjourney, Ideogram, or Firefly");
    } catch { toast.error("Couldn't copy — select the text manually."); }
  };

  return (
    <AppShell>
      <div className="py-4 md:py-8 w-full" data-testid="packaging-ai-page">
        <Link to="/ai-lab" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-shell-side">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to AI Lab
        </Link>
        <PageHero
          eyebrow="CPG AI Lab · Toolkit"
          title="Packaging AI"
          subtitle="Author packaging concepts that look retail-ready — hero mockups, on-shelf visuals, and brand-safe label variants — using best-in-class AI image tools guided by your Brand Profile."
        />

        {/* ── 1. OVERVIEW ─────────────────────────────────────────── */}
        <Section id="overview" icon={<Info className="w-4 h-4" />} title="Overview" data-testid="section-overview">
          <p className="text-sm text-muted-foreground max-w-prose">
            Packaging AI helps you generate on-brand hero mockups and shelf-ready visuals
            without hiring a studio for the first round. The workflow below is provider-agnostic
            — the Lab hands you a prompt calibrated to your brand, and you generate in
            whichever tool best matches your aesthetic and budget.
          </p>
          <div className="mt-4 grid sm:grid-cols-3 gap-3 text-sm">
            <StatChip label="Time saved" value="~4 hrs / concept" />
            <StatChip label="Est. cost" value="$0–20 per batch" />
            <StatChip label="Prereq" value={readyToBuild ? "Brand Profile ✓" : "Brand Profile needed"} warn={!readyToBuild} />
          </div>
          {!readyToBuild && (
            <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
              You haven't authored your Brand Profile yet — the Prompt Builder needs at
              least a brand name and category.{" "}
              <Link to="/ai-lab/brand-profile" className="font-semibold text-shell-side underline">
                Open Brand Profile →
              </Link>
            </div>
          )}
        </Section>

        {/* ── 2. WORKFLOW ─────────────────────────────────────────── */}
        <Section id="workflow" icon={<Layers className="w-4 h-4" />} title="Workflow" data-testid="section-workflow">
          <ol className="grid gap-3">
            {WORKFLOW.map((step, i) => (
              <li key={i} className="flex gap-3">
                <span className="shrink-0 w-7 h-7 rounded-full bg-shell-side/10 text-shell-side text-xs font-bold grid place-items-center">
                  {i + 1}
                </span>
                <div>
                  <p className="font-semibold text-sm">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.body}</p>
                  {step.cta && (
                    <Link to={step.cta.to}
                      className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-shell-side">
                      {step.cta.label} →
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </Section>

        {/* ── 3. RECOMMENDED TOOLS ────────────────────────────────── */}
        <Section id="tools" icon={<Sparkles className="w-4 h-4" />} title="Recommended tools"
          subtitle="Curated by the Warren team — pick one per column, not all five."
          data-testid="section-tools">
          <div className="grid md:grid-cols-3 gap-4">
            <ToolColumn icon={<FileText className="w-4 h-4" />} title="Text & copy"  tools={RECOMMENDED_TOOLS.text} />
            <ToolColumn icon={<Palette   className="w-4 h-4" />} title="Design & image" tools={RECOMMENDED_TOOLS.design} />
            <ToolColumn icon={<Video     className="w-4 h-4" />} title="Motion & video" tools={RECOMMENDED_TOOLS.video} />
          </div>
        </Section>

        {/* ── 4. PROMPT BUILDER ───────────────────────────────────── */}
        <Section id="prompt" icon={<Wand2 className="w-4 h-4" />} title="Prompt Builder"
          subtitle="Reads your Brand Profile and assembles a ready-to-paste prompt."
          data-testid="section-prompt-builder">
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="grid gap-3">
              <FieldRow label="Product type" hint="e.g. cold brew latte, mineral sunscreen, hair mask">
                <input value={productType} onChange={(e) => setProductType(e.target.value)}
                  data-testid="packaging-input-product-type"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
                  placeholder="cold brew adaptogen latte" />
              </FieldRow>
              <FieldRow label="Container">
                <select value={container} onChange={(e) => setContainer(e.target.value)}
                  data-testid="packaging-input-container"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
                  {["bottle","can","pouch","carton","jar","tube","box"].map((c) =>
                    <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Archetype" hint="Visual family the mockup should belong to">
                <select value={archetype} onChange={(e) => setArchetype(e.target.value)}
                  data-testid="packaging-input-archetype"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
                  {ARCHETYPES.map((a) => <option key={a.key} value={a.key}>{a.label} — {a.desc}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Surface / background">
                <select value={surfaceMood} onChange={(e) => setSurfaceMood(e.target.value)}
                  data-testid="packaging-input-surface"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
                  {["marble","linen","kitchen counter","bathroom shelf","gradient studio","natural wood","concrete"]
                    .map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Lighting">
                <select value={lighting} onChange={(e) => setLighting(e.target.value)}
                  data-testid="packaging-input-lighting"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
                  {["morning window","golden hour","overcast diffused","hard studio","editorial rim light","cool north-facing"]
                    .map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Aspect ratio">
                <select value={aspect} onChange={(e) => setAspect(e.target.value)}
                  data-testid="packaging-input-aspect"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm">
                  {["4:5","1:1","9:16","16:9","3:4"].map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldRow>
              <FieldRow label="Extras (optional)" hint="Prop, texture, model, or camera detail">
                <input value={extras} onChange={(e) => setExtras(e.target.value)}
                  data-testid="packaging-input-extras"
                  className="w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm"
                  placeholder="fresh mint sprigs, subtle steam, 50mm f/2.8 look" />
              </FieldRow>
            </div>

            <div className="rounded-2xl bg-shell-side/5 border border-shell-side/20 p-4 flex flex-col">
              <p className="text-[10px] uppercase tracking-widest text-shell-side font-bold">Assembled prompt</p>
              <textarea
                readOnly
                value={prompt}
                data-testid="packaging-prompt-output"
                className="mt-2 flex-1 min-h-[260px] w-full rounded-md border border-shell-border bg-white px-3 py-2 text-sm font-mono"
              />
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={copyPrompt}
                  data-testid="packaging-prompt-copy"
                  className="btn-primary text-sm inline-flex items-center gap-1.5">
                  <Copy className="w-4 h-4" /> Copy prompt
                </button>
                <a href="https://ideogram.ai" target="_blank" rel="noreferrer"
                  className="text-sm inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 hover:bg-muted/40"
                  data-testid="packaging-open-ideogram">
                  Open Ideogram <ExternalLink className="w-3.5 h-3.5" />
                </a>
                <a href="https://midjourney.com" target="_blank" rel="noreferrer"
                  className="text-sm inline-flex items-center gap-1.5 rounded-full border border-shell-border px-4 py-2 hover:bg-muted/40"
                  data-testid="packaging-open-midjourney">
                  Open Midjourney <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          </div>
        </Section>

        {/* ── 5. EXAMPLES ─────────────────────────────────────────── */}
        <Section id="examples" icon={<ImageIcon className="w-4 h-4" />} title="Examples"
          subtitle="Before-and-after prompts from Warren-team teardowns."
          data-testid="section-examples">
          <div className="grid gap-4">
            {EXAMPLES.map((ex, i) => (
              <div key={i} className="rounded-2xl border border-shell-border p-4"
                   data-testid={`packaging-example-${i}`}>
                <p className="text-[10px] uppercase tracking-widest text-shell-side font-bold">
                  {ex.archetype}
                </p>
                <p className="mt-2 text-sm"><span className="font-semibold">Before:</span> {ex.before}</p>
                <p className="mt-2 text-sm"><span className="font-semibold">After:</span> <span className="font-mono">{ex.after}</span></p>
                <p className="mt-2 text-xs text-muted-foreground italic">{ex.note}</p>
              </div>
            ))}
          </div>
        </Section>

      </div>
    </AppShell>
  );
}

// ── Prompt assembly ─────────────────────────────────────────────────
// Deliberately paragraph-form so it works in Midjourney (spaces + commas),
// Ideogram (structured phrases), and Firefly (natural-language description).
// The Brand Profile fields are threaded through so the resulting image is
// on-brand out of the gate.
function buildPrompt({ profile, archetype, productType, container, surfaceMood, lighting, aspect, extras }) {
  const brand = profile?.brand?.trim() || "your CPG brand";
  const category = profile?.category?.trim() || "consumer goods";
  const positioning = (profile?.positioning || "").trim();
  const consumer = (profile?.target_consumer || "").trim();
  const archetypeDesc = ARCHETYPES.find((a) => a.key === archetype)?.desc || "";
  const product = productType?.trim() || `${category} product`;

  const parts = [
    `Hero packaging mockup for "${brand}", a ${category} brand.`,
    `Product: ${product} in a ${container}.`,
    `Visual archetype: ${ARCHETYPES.find((a) => a.key === archetype)?.label || archetype} — ${archetypeDesc}`,
    positioning ? `Positioning: ${positioning}` : null,
    consumer ? `Speaks to: ${consumer}` : null,
    `Set on ${surfaceMood}, ${lighting} lighting, editorial food/lifestyle photography aesthetic.`,
    extras ? `Details: ${extras}.` : null,
    `Rendered ${aspect}, high resolution, retail-ready, natural depth of field, no text distortion.`,
  ].filter(Boolean);
  return parts.join("\n");
}

// ── UI primitives ───────────────────────────────────────────────────
function Section({ id, icon, title, subtitle, children, ...rest }) {
  return (
    <section id={id} className="mt-8 rounded-2xl border border-shell-border p-5 md:p-6 bg-white" {...rest}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 w-7 h-7 rounded-md bg-shell-side/10 text-shell-side grid place-items-center">
          {icon}
        </span>
        <div>
          <h2 className="font-display text-xl">{title}</h2>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function ToolColumn({ icon, title, tools }) {
  return (
    <div className="rounded-xl border border-shell-border p-3">
      <p className="text-[10px] uppercase tracking-widest font-bold text-shell-side flex items-center gap-1.5">
        {icon} {title}
      </p>
      <ul className="mt-3 space-y-3">
        {tools.map((t) => (
          <li key={t.name}>
            <a href={t.url} target="_blank" rel="noreferrer"
               className="group flex items-start gap-2 hover:text-shell-side"
               data-testid={`packaging-tool-${t.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <ExternalLink className="w-3.5 h-3.5 mt-0.5 shrink-0 opacity-40 group-hover:opacity-100" />
              <span>
                <span className="text-sm font-semibold">{t.name}</span>
                {t.pick && <span className="ml-1 text-[9px] uppercase tracking-widest font-bold text-primary align-middle">{t.pick}</span>}
                <span className="block text-xs text-muted-foreground">{t.blurb}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FieldRow({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      {hint && <span className="block text-xs text-muted-foreground">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}

function StatChip({ label, value, warn }) {
  return (
    <div className={"rounded-md border p-3 " + (warn ? "border-primary/30 bg-primary/5" : "border-shell-border")}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold mt-1">{value}</p>
    </div>
  );
}
